import type { RoleEnum } from '@zeus/index'
import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Moon, Sun } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { useTheme } from '~/components/ui/hooks/use-theme'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_LOGIN } from '~/gql/auth/auth'
import { useAuth } from '~/helpers/store/auth'
import { getDefaultRoute } from '~/routes/router'
import LoginSSO from './LoginSSO'

function Login() {
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const { t } = useTranslation()
  const { setPayload, setLoading } = useAuth()

  // Define the form schema with Zod
  const loginSchema = z.object({
    email: z.string().email({ message: t('input.email.message') }),
    password: z.string().min(1, { message: t('input.password') }),
  })

  type LoginFormValues = z.infer<typeof loginSchema>

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const [loginGql, { loading }] = useMutation(GQL_LOGIN)

  const onSubmit = async (values: LoginFormValues) => {
    try {
      const { data } = await loginGql({ variables: { email: values.email, password: values.password } })
      if (data?.login.__typename === 'UserInfo') {
        setPayload({
          firstName: data.login.firstName,
          lastName: data.login.lastName,
          email: data.login.email,
          role: data.login.roles,
          id: data.login.id,
        })
        setLoading(false)
        navigate(getDefaultRoute(data.login.roles as RoleEnum))
      }
    } catch {
      toast.error(t('bad.server-login.message'))
    }
  }

  if (loading) {
    return (
      <p className="text-center">
        {t('common.loading')}
        {' '}
        ...
      </p>
    )
  }

  return (
    <Card className="sm:w-md w-full border-0 shadow-none sm:border sm:shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="font-heading text-xs tracking-[0.3em] uppercase text-muted-foreground">Kering</span>
          <CardTitle className="font-heading text-2xl tracking-tight">{t('common.login')}</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          className="rounded-full"
        >
          {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full mx-auto">
                  <FormControl>
                    <div className="grid gap-2">
                      <Label htmlFor="email" className="font-heading text-sm tracking-wide">{t('common.email')}</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="m@example.com"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full mx-auto">
                  <FormControl>
                    <div className="grid gap-2">
                      <div className="flex items-center">
                        <Label htmlFor="password" className="font-heading text-sm tracking-wide">{t('common.password')}</Label>
                        <a
                          onClick={() => navigate('/reset-password')}
                          className="ml-auto inline-block text-sm underline-offset-4 hover:underline cursor-pointer"
                        >
                          {t('password.forgot')}
                        </a>
                      </div>
                      <Input
                        id="password"
                        type="password"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full font-heading tracking-wide uppercase text-sm">
              {t('common.login')}
            </Button>
            <LoginSSO />

            <div className="mt-4 text-center text-sm font-body">
              {`${t('have-account.message')} `}
              <a onClick={() => navigate('/create-account')} className="underline underline-offset-4 cursor-pointer">
                {t('common.register')}
              </a>
            </div>
          </form>
        </Form>

      </CardContent>
    </Card>
  )
}

export default Login
